import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';
import { SortOrder } from 'further-types/utils';

import Table from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';

import { Button, CircularProgress, Typography } from '@mui/material';
import Chip from 'components/Chip';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionOperationType } from 'further-types/lp/action';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import api from 'lib/trpcClient';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';
import ProcessingPage from 'components/new/ProcessingPage';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column, Row } from 'further-ui/components/Layout';

enum ApprovePrepaymentsTab {
  ResolvedPayments = 'resolvedPayments',
  ResolvedDeferredIncome = 'resolvedDeferredIncome',
}

const tabs = [
  { key: ApprovePrepaymentsTab.ResolvedPayments, title: 'Resolved payments' },
  {
    key: ApprovePrepaymentsTab.ResolvedDeferredIncome,
    title: 'Resolved deferred income',
  },
];

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

const pageId = 'approve-prepayments';

type Props = {
  onExit: () => void;
};

const ApprovePrepayments: FC<Props> = ({ onExit }) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const [canEdit] = useHasPermissions([RolePermission.LpEditAccountingPeriod]);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const { periodId } = useParams<{ periodId?: string }>();
  const history = useHistory();
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam ? new CalendarDay(periodEndParam) : null;
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [isProcessingUnapproval, setIsProcessingUnapproval] = useState(false);
  const [activeTab, setActiveTab] = useState<ApprovePrepaymentsTab>(
    ApprovePrepaymentsTab.ResolvedPayments,
  );
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const pagination = usePagination({
    id: pageId,
    order: SortOrder.Desc,
    orderBy: 'ledgerDate',
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const handleExit = () => {
    onExit();
  };

  const [period] = api.accountingPeriod.getPeriodById.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
    id: periodId ?? '',
  });
  const isConfirmed = !!period?.checklist?.prepaymentResolutions?.confirmed;

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: confirmChecklistItems } =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      mutationOpts({
        refetchType: 'active',
        onError: () => {
          setIsProcessingApproval(false);
          setIsProcessingUnapproval(false);
        },
        onSuccess: () =>
          history.push(createLpPeriodEndReviewDetailRoute(periodId ?? '')),
      }),
    );

  const handleApprove = async () => {
    if (!selectedVehicleId || !periodId) return;
    setIsProcessingApproval(true);
    await confirmChecklistItems({
      vehicleId: selectedVehicleId,
      id: periodId,
      items: ['prepaymentResolutions'],
      confirmed: true,
    });
  };

  const handleUnapprove = async () => {
    if (!selectedVehicleId || !periodId) return;
    setIsProcessingUnapproval(true);
    await confirmChecklistItems({
      vehicleId: selectedVehicleId,
      id: periodId,
      items: ['prepaymentResolutions'],
      confirmed: false,
    });
  };

  const queryEnabled = !!periodStart && !!periodEnd;
  const periodParams = {
    vehicleId: selectedVehicleId ?? '',
    periodStart: periodStart ?? undefined,
    periodEnd: periodEnd ?? undefined,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
  };

  const { data: resolvedPaymentsData, isFetching: resolvedPaymentsLoading } =
    api.prepayments.listPrepayments.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Spent },
      { enabled: queryEnabled },
    );

  const {
    data: resolvedDeferredIncomeData,
    isFetching: resolvedDeferredIncomeLoading,
  } = api.prepayments.listPrepayments.useQuery(
    { ...periodParams, actionOperationType: ActionOperationType.Received },
    { enabled: queryEnabled },
  );

  const tabData = {
    [ApprovePrepaymentsTab.ResolvedPayments]: resolvedPaymentsData,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]: resolvedDeferredIncomeData,
  };

  const tabCounts = {
    [ApprovePrepaymentsTab.ResolvedPayments]:
      resolvedPaymentsData?.totalCount ?? 0,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]:
      resolvedDeferredIncomeData?.totalCount ?? 0,
  };

  const tabLoading = {
    [ApprovePrepaymentsTab.ResolvedPayments]: resolvedPaymentsLoading,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]:
      resolvedDeferredIncomeLoading,
  };

  const data = tabData[activeTab];

  const handleTabChange = (tab: ApprovePrepaymentsTab) => {
    setExpandedRowId(null);
    setActiveTab(tab);
    pagination.handleChangeSort('ledgerDate', SortOrder.Desc);
  };

  const rowsForTable = (data?.results ?? []).map((row) => ({
    ...row,
    id: row.id ?? '',
  }));

  const columns = [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'amountTcy',
      label: 'Amount (TCY)',
      render: (row) => numberToCurrencyString(row.amountTcy, { fallback: '-' }),
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) => numberToCurrencyString(row.amountLcy, { fallback: '-' }),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
  ];

  return (
    <FullPageDrawer
      open
      size="lg"
      innerScroll
      onClose={handleExit}
      title="Review prepayment resolutions"
      actions={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          {periodId && isConfirmed && canEdit && (
            <Button
              size="large"
              variant="outlined"
              color="error"
              onClick={handleUnapprove}
            >
              Unmark as reviewed
            </Button>
          )}
          {periodId && !isConfirmed && canEdit && (
            <Button size="large" variant="contained" onClick={handleApprove}>
              Mark as reviewed
            </Button>
          )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {isProcessingApproval ? (
          <ProcessingPage
            title="Marking section as reviewed"
            subtitle="This will only take a moment."
          />
        ) : isProcessingUnapproval ? (
          <ProcessingPage
            title="Unmarking section as reviewed"
            subtitle="This will only take a moment."
          />
        ) : (
          <>
            <Typography variant="h5">Prepayment resolutions</Typography>
            <Typography variant="body2">
              Review prepayment resolutions from {periodStart?.toLabel()} –{' '}
              {periodEnd?.toLabel()}.
            </Typography>
            <TabsPanel
              tabs={tabs.map(({ key, title }) => ({
                key,
                label: (
                  <TabLabelWrapper>
                    {title}
                    {tabLoading[key] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Chip
                        variant="tabIndicator"
                        small
                        bold={false}
                        empty={tabCounts[key] === 0 || isConfirmed}
                      >
                        {tabCounts[key]}
                      </Chip>
                    )}
                  </TabLabelWrapper>
                ),
                content:
                  tabCounts[key] === 0 ? null : (
                    <>
                      <Table
                        columns={columns}
                        rows={rowsForTable}
                        pagination={{
                          page: pagination.page,
                          rowsPerPage: pagination.rowsPerPage,
                          order: pagination.order,
                          orderBy: pagination.orderBy,
                          handleRequestSort: pagination.handleRequestSort,
                          handleChangePage: pagination.handleChangePage,
                          handleChangeRowsPerPage:
                            pagination.handleChangeRowsPerPage,
                        }}
                        rowsTotalCount={data?.totalCount ?? 0}
                        isExpandableRow={() => true}
                        isExpandedRow={(row) => expandedRowId === row.id}
                        onRowExpand={(row) =>
                          setExpandedRowId((prev) =>
                            prev === row.id ? null : row.id,
                          )
                        }
                        getExpandedContent={(row) => (
                          <ExpandedRowContainer>
                            {!transactionTypesLoading && vehicle && (
                              <TransactionAllocator
                                showCurrencyRate
                                transactionTypeOptions={transactionTypeOptions}
                                transactions={row.transactions ?? []}
                                readOnly
                                transactionCurrency={row.currencyCode}
                                vehicle={vehicle}
                                fxRate={null}
                                darkBackground
                              />
                            )}
                          </ExpandedRowContainer>
                        )}
                      />
                    </>
                  ),
              }))}
              onTabChange={handleTabChange}
              defaultTab={ApprovePrepaymentsTab.ResolvedPayments}
            />
          </>
        )}
      </Column>
    </FullPageDrawer>
  );
};

export default ApprovePrepayments;
